import { MetricType, SystemMetric } from "@aibotdev/ct-analytics"

export interface LongTaskData {
    /** Duration of the task in milliseconds */
    duration: number
    /** Start time of the task */
    startTime: number
    /** Name/identifier of the component that generated the task */
    name: string
}

export interface AggregatedLongTaskMetrics {
    /** Total Blocking Time - sum of all tasks >50ms */
    tbt: number
    /** Maximum task duration in the period */
    maxDuration: number
    /** Count of tasks >50ms */
    count50: number
    /** Count of tasks >200ms (noticeable to users) */
    count200: number
    /** Count of tasks >500ms (critical lags) */
    count500: number
}

type MetricsFlushCallback = (metrics: SystemMetric[]) => void

export class LongTaskAggregator {
    private buffer: LongTaskData[] = []

    constructor(private onFlush: MetricsFlushCallback) {}

    addLongTask(task: LongTaskData) {
        this.buffer.push(task)
    }

    private createSystemMetric(aggregates: AggregatedLongTaskMetrics, taskName: string): SystemMetric {
        return {
            Name: `${taskName}-longtask`,
            Type: MetricType.Histogram,
            Value: aggregates.count50,
            Labels: {
                tbt: aggregates.tbt.toString(),
                max: aggregates.maxDuration.toString(),
                count_50: aggregates.count50.toString(),
                count_200: aggregates.count200.toString(),
                count_500: aggregates.count500.toString()
            }
        }
    }

    private flushBuffer() {
        if (this.buffer.length === 0) return

        const taskGroups = new Map<string, LongTaskData[]>()

        for (const task of this.buffer) {
            if (!taskGroups.has(task.name)) {
                taskGroups.set(task.name, [])
            }
            const group = taskGroups.get(task.name)
            if (group) {
                group.push(task)
            }
        }

        const metrics: SystemMetric[] = []

        for (const [taskName, tasks] of Array.from(taskGroups.entries())) {
            let tbt = 0
            let maxDuration = 0
            let count50 = 0
            let count200 = 0
            let count500 = 0

            for (const task of tasks) {
                const duration = task.duration

                if (duration > 50) {
                    tbt += duration
                    count50++

                    if (duration > 200) {
                        count200++

                        if (duration > 500) {
                            count500++
                        }
                    }
                }

                maxDuration = Math.max(maxDuration, duration)
            }

            const taskAggregates: AggregatedLongTaskMetrics = {
                tbt,
                maxDuration,
                count50,
                count200,
                count500
            }

            metrics.push(this.createSystemMetric(taskAggregates, taskName))
        }

        this.buffer = []

        if (metrics.length > 0) {
            this.onFlush(metrics)
        }
    }

    flush() {
        this.flushBuffer()
    }

    destroy() {
        this.buffer = []
    }
}
